import * as THREE from 'three';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { HUD } from '../ui/hud.js';
import { COSMETICS } from '../entities/cosmetics.js';
import { getCrumbs, ownsCosmetic, getEquipped, buyCosmetic, equipCosmetic, unequipSlot } from '../core/save.js';

// Toast Threads — downtown's clothing store, and a real shop: walk up to the
// racks to open the boutique and spend crumbs on hats & ties. Bought cosmetics
// go on Bread Boi immediately (and everywhere else, via save.wardrobe). The
// mannequins/mirror are still flavor; exit heads back to downtown.

export function createClothingScene(){
  return () => new ClothingScene();
}

class ClothingScene {
  constructor(){
    this.poseT = 0;
    this.panels = [];
    this.shopOpen = false;
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xE7D7EC);

    this.camera = new THREE.PerspectiveCamera(52, window.innerWidth/window.innerHeight, 0.1, 100);
    this.camDist = 10;
    this.camTarget = new THREE.Vector3(0, 1.6, 0);
    this.azimuth = 0; this.polar = 0.46;

    this._buildRoom();
    this._buildStock();

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 4);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    this.hotspots = [
      { pos: new THREE.Vector3(-4.5, 0, -3), r: 2.0, label: '🧢 shop the hat wall', action: () => this._openShop('hat') },
      { pos: new THREE.Vector3(0, 0, -3.6), r: 2.0, label: '👔 shop the racks', action: () => this._openShop() },
      { pos: new THREE.Vector3(3.6, 0, -2.6), r: 2.0, label: 'check the mannequins', say: 'the mannequins are serving looks. inspired, Toast heads for the racks.' },
      { pos: new THREE.Vector3(5.4, 0, 1.2), r: 1.8, label: 'look in the mirror', say: 'lookin\' crispy! whatever you\'re wearing, you\'re wearing it well.' },
      { pos: new THREE.Vector3(0, 0, 5.4), r: 2.2, label: '← back to downtown', action: () => this.manager.goTo('downtown', { from: 'clothing' }) }
    ];
    this.nearSpot = null;
  }

  _buildRoom(){
    this.scene.add(new THREE.AmbientLight(0xFFFFFF, 0.85));
    const key = new THREE.DirectionalLight(0xFFF6E8, 0.5);
    key.position.set(4, 10, 6); key.castShadow = true; key.shadow.mapSize.set(1024, 1024);
    this.scene.add(key);

    const floor = new THREE.Mesh(new THREE.PlaneGeometry(14, 14), new THREE.MeshLambertMaterial({ color: 0xC9A66B }));
    floor.rotation.x = -Math.PI/2; floor.receiveShadow = true; this.scene.add(floor);
    const back = new THREE.Mesh(new THREE.PlaneGeometry(14, 8), new THREE.MeshLambertMaterial({ color: 0xB88FC4 }));
    back.position.set(0, 4, -6); this.scene.add(back);
    const sideL = back.clone(); sideL.rotation.y = Math.PI/2; sideL.position.set(-7, 4, 0); this.scene.add(sideL);
    const sideR = back.clone(); sideR.rotation.y = -Math.PI/2; sideR.position.set(7, 4, 0); this.scene.add(sideR);

    const marquee = textSprite('TOAST THREADS', 30, '#FFF4DF', 4.4, 1.1);
    marquee.position.set(0, 3.4, -5.7); this.scene.add(marquee);
    this.exitDoor = box(1.5, 2.4, 0.3, C.crustDark, 0, 1.2, 5.9, this.scene);
    const sign = textSprite('← to downtown', 24, '#3A2A1C', 2.8, 0.8);
    sign.position.set(0, 2.9, 5.7); this.scene.add(sign);
  }

  _buildStock(){
    const HAT = [C.raspberry, C.cobalt, C.sprout, C.tangerine, C.toast];
    // hat wall shelves (back-left)
    for (let r = 0; r < 3; r++) for (let i = 0; i < 4; i++){
      cyl(0.22, 0.26, 0.28, HAT[(r + i) % HAT.length], -5.6 + i * 0.7, 1.2 + r * 0.9, -5.7, this.scene, 12);
    }
    // shirt rack (a bar of hanging garments) center-back
    const rack = new THREE.Group(); rack.position.set(0, 0, -4.4); this.scene.add(rack);
    cyl(0.04, 0.04, 2.4, C.crustDark, -1.2, 1.4, 0, rack, 6);
    cyl(0.04, 0.04, 2.4, C.crustDark, 1.2, 1.4, 0, rack, 6);
    const bar = cyl(0.03, 0.03, 2.6, 0x999999, 0, 1.9, 0, rack, 6); bar.rotation.z = Math.PI/2;
    for (let i = 0; i < 6; i++) box(0.34, 0.8, 0.12, HAT[i % HAT.length], -1.0 + i * 0.4, 1.35, 0, rack);

    // mannequins (right-back), each "wearing" a hat + tie
    const mannequin = (x, hat, tie) => {
      const g = new THREE.Group(); g.position.set(x, 0, -2.8); this.scene.add(g);
      cyl(0.16, 0.22, 0.1, C.crustDark, 0, 0.05, 0, g, 12);        // base
      cyl(0.05, 0.05, 1.2, 0xE8DCC8, 0, 0.7, 0, g, 8);             // stand
      const torso = box(0.6, 0.8, 0.35, 0xEFE3D0, 0, 1.5, 0, g);
      sphere(0.26, 0xEFE3D0, 0, 2.05, 0, g, 12);                   // head
      cyl(0.28, 0.32, 0.22, hat, 0, 2.35, 0, g, 12);               // hat
      box(0.1, 0.34, 0.04, tie, 0, 1.5, 0.19, g);                  // tie
    };
    mannequin(3.0, C.raspberry, C.cobalt); mannequin(4.2, C.sprout, C.tangerine);

    // fitting mirror (right side)
    const frame = box(1.0, 2.0, 0.15, C.rye, 5.6, 1.2, 1.2, this.scene);
    box(0.8, 1.8, 0.05, C.breeze, 5.68, 1.2, 1.2, this.scene);

    // checkout counter (left)
    box(1.6, 1.1, 0.9, C.crust, -5.2, 0.55, 1.5, this.scene);
    box(1.7, 0.12, 1.0, C.crustDark, -5.2, 1.15, 1.5, this.scene);
  }

  // ---- lifecycle ----
  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.28, 0.62];
    this.input.onTap = (ndc, ray) => this._tap(ray);
    HUD.setSubtitle('toast threads');
    HUD.say('Toast Threads! Walk up to the racks to spend crumbs on hats & ties — you\'ll wear them right away. Exit to downtown.');
    HUD.setHint('click to walk · walk up to the racks to shop, or the exit');
    HUD.hideHintSoon(7000);
  }

  exit(){ this._clearPanels(); this.input.onTap = null; HUD.showEnter(null); HUD.say(''); }

  // ---- boutique shop (DOM overlay) ----
  _panel(html){
    const el = document.createElement('div');
    el.className = 'cafe-panel';           // reuse the modal backdrop
    el.innerHTML = html;
    document.body.appendChild(el);
    this.panels.push(el);
    return el;
  }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  _openShop(){
    this._clearPanels();
    this.shopOpen = true;
    this.nearSpot = null;
    this.input.onTap = null;               // pause walking while the shop is open
    HUD.showEnter(null);
    const wrap = this._panel(`
      <div class="shop-card">
        <div class="shop-head">
          <h2>Toast Threads</h2>
          <div class="shop-bal">🍞 <b id="shopBal">${getCrumbs()}</b></div>
        </div>
        <div class="shop-list"></div>
        <button class="cafe-btn ghost shop-close">← done shopping</button>
      </div>`);
    wrap.querySelector('.shop-close').onclick = () => this._closeShop();
    this._renderShop(wrap);
  }

  _renderShop(wrap){
    wrap.querySelector('#shopBal').textContent = getCrumbs();
    const list = wrap.querySelector('.shop-list');
    list.innerHTML = '';
    for (const item of COSMETICS){
      const owned = ownsCosmetic(item.id);
      const equipped = getEquipped(item.slot) === item.id;
      const afford = getCrumbs() >= item.price;

      const row = document.createElement('div');
      row.className = 'shop-row' + (equipped ? ' on' : '');
      let btnHtml;
      if (!owned) btnHtml = `<button class="shop-buy"${afford ? '' : ' disabled'}>🍞 ${item.price}</button>`;
      else if (equipped) btnHtml = `<button class="shop-eq worn">worn ✓</button>`;
      else btnHtml = `<button class="shop-eq">wear</button>`;
      row.innerHTML =
        `<span class="shop-sw" style="background:${item.swatch}">${item.tag}</span>` +
        `<span class="shop-name">${item.name}<small>${item.slot}</small></span>` +
        btnHtml;

      const btn = row.querySelector('button');
      if (!owned){
        btn.onclick = () => {
          if (buyCosmetic(item.id, item.price)){       // buy → wear straight away
            equipCosmetic(item.slot, item.id);
            this._onWardrobeChange();
            this._renderShop(wrap);
          }
        };
      } else if (equipped){
        btn.onclick = () => { unequipSlot(item.slot); this._onWardrobeChange(); this._renderShop(wrap); };
      } else {
        btn.onclick = () => { equipCosmetic(item.slot, item.id); this._onWardrobeChange(); this._renderShop(wrap); };
      }
      list.appendChild(row);
    }
  }

  _onWardrobeChange(){
    this.bread.applyWardrobe();            // update the toast standing in the shop
    this.bread.drawFace('yum'); this.poseT = 1.0;
    HUD.refreshCrumbs();
  }

  _closeShop(){
    this._clearPanels();
    this.shopOpen = false;
    this.input.onTap = (ndc, ray) => this._tap(ray);
  }

  _tap(ray){
    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(p.x, 0, p.z);
    gp.x = Math.max(-6, Math.min(6, gp.x));
    gp.z = Math.max(-2.4, Math.min(5.4, gp.z));
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  update(dt){
    this.bread.update(dt);
    if (this.poseT > 0){
      this.poseT -= dt;
      this.bread.armL.shoulder.rotation.x = -1.2;
      if (this.poseT <= 0){ this.bread.resetLimbs(); this.bread.drawFace('happy'); }
    }

    if (!this.shopOpen){
      let near = null, nd = Infinity;
      this.hotspots.forEach(h => { const d = h.pos.distanceTo(this.bread.root.position); if (d < nd){ nd = d; near = h; } });
      if (near && nd < near.r){
        if (this.nearSpot !== near){
          this.nearSpot = near;
          HUD.showEnter(near.label, () => {
            if (near.action) near.action();
            else { HUD.say(near.say); this.bread.drawFace('grin'); this.poseT = 1.4; }
          });
        }
      } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }
    }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.4, 1.6, this.bread.root.position.z * 0.4), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
